import React, { useState, useEffect } from 'react';
import {
  ReferenceInput,
  SelectInput,
  required,
  useRecordContext,
  TextInput,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { SeatingPlanEditor } from '../../seating';
import { useDataProvider } from 'react-admin';
import { getEventLayoutType } from '../../../utils/eventFormUtils';
import { getEventDbId } from '../../../utils/eventIdentity';

/**
 * Event Layout Tab Component
 * Handles layout selection for area and seated events
 */
export const EventLayoutTab = ({ eventType, isEdit = false }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { getValues } = useFormContext();
  const layoutType = getEventLayoutType(eventType);
  const record = useRecordContext();
  const venue = useWatch({ name: 'venue' });
  const formLayoutID = useWatch({ name: 'layoutID' });
  const formTickets = useWatch({ name: 'tickets' });
  const [ticketsData, setTicketsData] = useState(null);
  const [layoutJSONData, setLayoutJSONData] = useState(null);
  const [layoutLoadDone, setLayoutLoadDone] = useState(!isEdit);

  // Load tickets data and layoutJSON from stocks and eventLayouts collections in edit mode
  useEffect(() => {
    if (isEdit && record && (record.id || record.uid)) {
      const loadEventData = async () => {
        setLayoutLoadDone(false);
        try {
          // Prefer unsaved form value when remounting after tab switch
          const existingLayoutJSON = getValues('layoutJSON');
          if (existingLayoutJSON) {
            setLayoutJSONData(existingLayoutJSON);
          }

          const { data: stocksData } = await dataProvider.getList('stocks', {
            filter: { event: getEventDbId(record) },
            pagination: { page: 1, perPage: 1000 },
          });

          if (stocksData && stocksData.length > 0) {
            const stock = stocksData[0];
            if (eventType === 3) {
              setTicketsData(stock.stock || []);
            } else {
              setTicketsData(stock.stock || {});
            }
          } else {
            setTicketsData(eventType === 3 ? [] : {});
          }

          if (!existingLayoutJSON) {
            // Once layoutJSONID (event-specific plan). Yoksa null → editor base layoutID yukler.
            const eventLayoutID = record.layoutJSONID;
            if (eventLayoutID) {
              try {
                const { data: eventLayoutData } = await dataProvider.getOne('eventLayouts', {
                  id: eventLayoutID,
                });
                if (eventLayoutData?.layoutJSON) {
                  setLayoutJSONData(eventLayoutData.layoutJSON);
                } else {
                  setLayoutJSONData(null);
                }
              } catch (error) {
                console.warn('eventLayouts load failed, fallback to base layout', error);
                setLayoutJSONData(null);
              }
            } else {
              setLayoutJSONData(null);
            }
          }
        } catch (error) {
          console.error('Error loading event data:', error);
          setTicketsData(eventType === 3 ? [] : {});
          if (!getValues('layoutJSON')) {
            setLayoutJSONData(null);
          }
        } finally {
          setLayoutLoadDone(true);
        }
      };

      loadEventData();
    }
  }, [isEdit, record, eventType, dataProvider, getValues]);

  const layoutLabel = translate('form.layout', { _: 'Layout' });
  const emptyLayoutText = translate(
    eventType === 2 ? 'form.select_area_layout' : 'form.select_seating_layout',
  );

  if (!venue) {
    return (
      <React.Fragment>
        <h3>{layoutLabel}</h3>
        <TextInput source="layoutJSON" sx={{ display: 'none' }} defaultValue={null} />
        <TextInput source="layoutJSONID" sx={{ display: 'none' }} defaultValue={null} />
        <span style={{ fontSize: 14, marginTop: -10, marginBottom: 20 }}>
          {translate('form.venue_required_for_layouts')}
        </span>
      </React.Fragment>
    );
  }

  const filter = { venue, layoutType: layoutType };
  const layoutID = isEdit ? record.layoutID : formLayoutID;
  const showEditor = Boolean(layoutID) && (!isEdit || (layoutLoadDone && ticketsData !== null));
  const editorRecord = isEdit ? layoutJSONData || layoutID : layoutID;
  const hasAssignedPlan = Boolean(isEdit && layoutJSONData);

  return (
    <React.Fragment>
      <h3>{layoutLabel}</h3>
      {/* Form'a register olsun diye gizli alan — yoksa setValue Save'de kayboluyor */}
      <TextInput source="layoutJSON" sx={{ display: 'none' }} defaultValue={null} />
      <TextInput source="layoutJSONID" sx={{ display: 'none' }} defaultValue={null} />

      {layoutID ? (
        <React.Fragment>
          <ReferenceInput
            source="layoutID"
            label={layoutLabel}
            reference="layouts"
            filter={filter}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <SelectInput
              validate={required()}
              optionText="name"
              fullWidth
              label={layoutLabel}
              disabled={isEdit}
              emptyText={emptyLayoutText}
            />
          </ReferenceInput>
          {isEdit && layoutLoadDone && !layoutJSONData && (
            <div style={{ marginBottom: 10, color: '#b45309', fontSize: 13 }}>
              {translate('form.no_assigned_seats_warning')}
            </div>
          )}
          {showEditor && (
            <SeatingPlanEditor
              key={`${hasAssignedPlan ? 'assigned' : 'base'}-${layoutID}-${isEdit ? 'edit' : 'create'}`}
              record={editorRecord}
              fullLayout={hasAssignedPlan}
              isAssignMode={true}
              tickets={isEdit ? ticketsData : formTickets}
              dataProvider={dataProvider}
              isStandardEvent={true}
              layoutType={layoutType}
            />
          )}
        </React.Fragment>
      ) : (
        <ReferenceInput
          source="layoutID"
          label={layoutLabel}
          reference="layouts"
          filter={filter}
          sort={{ field: 'name', order: 'ASC' }}
        >
          <SelectInput
            validate={required()}
            optionText="name"
            fullWidth
            label={layoutLabel}
            emptyText={emptyLayoutText}
          />
        </ReferenceInput>
      )}
    </React.Fragment>
  );
};
